import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { VIDEO_GUIDE_URL } from "../lib/assets";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogTrigger } from "../components/ui/dialog";
import { useToast } from "../hooks/use-toast";
import { positions, countries } from "../mock/mock";
import { Plus, Trash2, Video, BookOpen, Loader2, Save, ShieldCheck, ArrowLeft } from "lucide-react";

const empty = { name: "", position: "", city: "", country: "", height: "", foot: "Right", birthYear: "", club: "", bio: "", openForTrial: false, videos: [] };

const AdminEditPlayer = () => {
  const { id } = useParams();
  const { t, user, authLoading, isAdmin } = useApp();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState(empty);
  const [vTitle, setVTitle] = useState("");
  const [vUrl, setVUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
    if (!authLoading && user && !isAdmin) navigate("/dashboard");
  }, [user, authLoading, isAdmin, navigate]);

  useEffect(() => {
    api.get(`/players/${id}`).then(({ data }) => {
      if (data) {
        setForm({
          name: data.name || "", position: data.position || "", city: data.city || "",
          country: data.country || "", height: data.height || "", foot: data.foot || "Right",
          birthYear: data.birthYear || "", club: data.club || "", bio: data.bio || "",
          openForTrial: !!data.openForTrial, videos: data.videos || [],
        });
      }
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const addVideo = () => {
    if (!vUrl.trim()) return;
    setForm((f) => ({ ...f, videos: [...f.videos, { title: vTitle.trim() || "Video", url: vUrl.trim() }] }));
    setVTitle(""); setVUrl("");
  };
  const removeVideo = (i) => setForm((f) => ({ ...f, videos: f.videos.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    if (!form.name || !form.position) return toast({ description: "Name and position required" });
    setSaving(true);
    try {
      await api.put(`/admin/players/${id}`, {
        ...form,
        height: parseInt(form.height) || 0,
        birthYear: parseInt(form.birthYear) || 0,
      });
      toast({ description: t.myprofile.saved });
      navigate(`/player/${id}`);
    } catch {
      toast({ description: t.payment.failed });
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "bg-[#0a0c10] border-white/10 text-gray-200 h-11 focus-visible:ring-blue-500";
  const selCls = "bg-[#0a0c10] border-white/10 text-gray-200 h-11 focus:ring-blue-500";
  const contentCls = "bg-[#111318] border-white/10 text-gray-200";

  if (!loaded) {
    return <div className="pt-32 min-h-screen text-center"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>;
  }

  return (
    <div className="pt-16 min-h-screen">
      <div className="border-b border-white/5 bg-[#0a0c10]">
        <div className="max-w-4xl mx-auto px-5 py-10">
          <button onClick={() => navigate(`/player/${id}`)} className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1.5 mb-4">
            <ArrowLeft className="w-4 h-4" /> {t.player.back}
          </button>
          <h1 className="font-display text-3xl font-bold text-white flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/30 px-2.5 py-1 rounded-full">
              <ShieldCheck className="w-3.5 h-3.5" /> ADMIN
            </span>
            {t.myprofile.title}
          </h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 py-8 space-y-6">
        <div className="bg-[#0f1116] border border-white/8 rounded-2xl p-6 grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm text-gray-400 mb-1.5 block">{t.myprofile.name}</Label>
            <Input value={form.name} onChange={set("name")} className={inputCls} />
          </div>
          <div>
            <Label className="text-sm text-gray-400 mb-1.5 block">{t.myprofile.position}</Label>
            <Select value={form.position} onValueChange={(v) => setForm((f) => ({ ...f, position: v }))}>
              <SelectTrigger className={selCls}><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className={contentCls}>{positions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm text-gray-400 mb-1.5 block">{t.myprofile.city}</Label>
            <Input value={form.city} onChange={set("city")} className={inputCls} />
          </div>
          <div>
            <Label className="text-sm text-gray-400 mb-1.5 block">{t.myprofile.country}</Label>
            <Select value={form.country} onValueChange={(v) => setForm((f) => ({ ...f, country: v }))}>
              <SelectTrigger className={selCls}><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className={contentCls}>{countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm text-gray-400 mb-1.5 block">{t.myprofile.height}</Label>
            <Input type="number" value={form.height} onChange={set("height")} className={inputCls} />
          </div>
          <div>
            <Label className="text-sm text-gray-400 mb-1.5 block">{t.myprofile.foot}</Label>
            <Select value={form.foot} onValueChange={(v) => setForm((f) => ({ ...f, foot: v }))}>
              <SelectTrigger className={selCls}><SelectValue /></SelectTrigger>
              <SelectContent className={contentCls}>
                <SelectItem value="Left">Left</SelectItem>
                <SelectItem value="Right">Right</SelectItem>
                <SelectItem value="Both">Both</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm text-gray-400 mb-1.5 block">{t.myprofile.birthYear}</Label>
            <Input type="number" value={form.birthYear} onChange={set("birthYear")} className={inputCls} />
          </div>
          <div>
            <Label className="text-sm text-gray-400 mb-1.5 block">{t.myprofile.club}</Label>
            <Input value={form.club} onChange={set("club")} className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-sm text-gray-400 mb-1.5 block">{t.myprofile.bio}</Label>
            <Textarea value={form.bio} onChange={set("bio")} className="bg-[#0a0c10] border-white/10 text-gray-200 min-h-[100px] focus-visible:ring-blue-500" />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.openForTrial} onCheckedChange={(v) => setForm((f) => ({ ...f, openForTrial: v }))} className="data-[state=checked]:bg-emerald-500" />
            <Label className="text-sm text-gray-300">{t.myprofile.openTrial}</Label>
          </div>
        </div>

        <div className="bg-[#0f1116] border border-white/8 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h3 className="font-semibold text-white flex items-center gap-2"><Video className="w-4 h-4 text-blue-400" /> {t.myprofile.videos}</h3>
            <Dialog>
              <DialogTrigger asChild>
                <button className="inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 bg-blue-500/10 border border-blue-500/20 rounded-lg px-3 py-1.5">
                  <BookOpen className="w-4 h-4" /> {t.myprofile.guide}
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[#0b0d11] border-white/10 max-w-5xl max-h-[90vh] overflow-y-auto p-2">
                <img src={VIDEO_GUIDE_URL} alt="Video Guide" className="w-full rounded-lg" />
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid sm:grid-cols-[1fr_1.4fr_auto] gap-3 items-end">
            <div>
              <Label className="text-sm text-gray-400 mb-1.5 block">{t.myprofile.videoTitle}</Label>
              <Input value={vTitle} onChange={(e) => setVTitle(e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label className="text-sm text-gray-400 mb-1.5 block">{t.myprofile.videoUrl}</Label>
              <Input value={vUrl} onChange={(e) => setVUrl(e.target.value)} placeholder="https://..." className={inputCls} />
            </div>
            <Button onClick={addVideo} className="bg-blue-600 hover:bg-blue-500 text-white h-11 rounded-xl">
              <Plus className="w-4 h-4 mr-1" /> {t.myprofile.addVideo}
            </Button>
          </div>
          <div className="mt-5 space-y-2">
            {form.videos.length === 0 ? (
              <p className="text-sm text-gray-500">{t.myprofile.noVideos}</p>
            ) : form.videos.map((v, i) => (
              <div key={i} className="flex items-center justify-between bg-[#0a0c10] border border-white/8 rounded-xl px-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm text-gray-200 truncate">{v.title}</div>
                  <div className="text-xs text-gray-500 truncate">{v.url}</div>
                </div>
                <button onClick={() => removeVideo(i)} className="text-red-400 hover:text-red-300 shrink-0 ml-3">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-500 text-white h-11 px-8 rounded-xl font-medium">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> {t.myprofile.save}</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminEditPlayer;
